import { useState, useEffect, useMemo } from 'react';
import { Calendar, Users, CheckCircle2, XCircle, Clock, AlertTriangle, Save, Filter, TrendingUp } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

function getToday() {
  return new Date().toISOString().split('T')[0];
}

function getMinDate() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split('T')[0];
}

function getInitials(name) {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'ST';
}

export function AttendancePage() {
  const { currentUser, isAdmin } = useApp();
  const isViewOnly = isAdmin;
  const canEdit = currentUser?.role === 'teacher';

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [dateError, setDateError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [attPeriod, setAttPeriod] = useState('month');
  const [attPercentage, setAttPercentage] = useState(null);
  const [classSections, setClassSections] = useState([]);

  useEffect(() => {
    loadClassSections();
  }, []);

  useEffect(() => {
    if (currentUser?.role === 'teacher' && currentUser.assignedClasses?.length > 0) {
      const first = currentUser.assignedClasses[0];
      if (first.includes('-')) {
        const [cls, sec] = first.split('-');
        setSelectedClass(cls);
        setSelectedSection(sec);
      }
    }
    loadAttPercentage();
  }, [currentUser]);

  useEffect(() => { loadAttPercentage(); }, [attPeriod]);

  const loadClassSections = async () => {
    try {
      const data = await api.getClassSections();
      setClassSections(data);
    } catch (e) { console.error(e); }
  };

  const loadAttPercentage = async () => {
    try {
      const data = await api.getAttendancePercentage(attPeriod);
      setAttPercentage(data);
    } catch (e) { console.error(e); }
  };

  // Validate T-7 rule on date change
  const handleDateChange = (e) => {
    const val = e.target.value;
    const selected = new Date(val);
    const today = new Date(getToday());
    const minAllowed = new Date(getMinDate());
    
    if (selected > today) {
      setDateError('Cannot mark attendance for future dates');
      return;
    }
    if (selected < minAllowed) {
      setDateError('Attendance cannot be marked beyond allowed past date limit (7 days)');
      return;
    }
    setDateError('');
    setSelectedDate(val);
  };

  // Dynamic classes and sections from DB
  const availableClasses = useMemo(() => {
    if (currentUser?.role === 'teacher') {
      return [...new Set(currentUser.assignedClasses?.map(c => c.split('-')[0]) || [])];
    }
    return [...new Set(classSections.map(cs => cs.class))].sort();
  }, [currentUser, classSections]);

  const availableSections = useMemo(() => {
    if (!selectedClass) return [];
    if (currentUser?.role === 'teacher') {
      return [...new Set(currentUser.assignedClasses?.filter(c => c.startsWith(selectedClass + '-')).map(c => c.split('-')[1]) || [])];
    }
    return classSections.filter(cs => cs.class === selectedClass).map(cs => cs.section).sort();
  }, [currentUser, classSections, selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection && selectedDate) {
      fetchAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSection, selectedDate]);

  const fetchAttendance = async () => {
    setIsLoading(true);
    setHasChanges(false);
    try {
      const data = await api.getClassAttendance(selectedClass, selectedSection, selectedDate);
      setRecords(data.records.map(r => ({
        studentId: r.studentId,
        fullName: r.fullName,
        rollNumber: r.rollNumber,
        status: r.status === 'not_marked' ? (canEdit ? 'present' : 'not_marked') : r.status,
        attendancePercentage: r.attendancePercentage,
      })).sort((a, b) => (a.rollNumber || 999) - (b.rollNumber || 999)));
    } catch (error) {
      toast.error(error.message || 'Failed to fetch attendance');
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = (studentId, newStatus) => {
    if (isViewOnly) return;
    setRecords(prev => prev.map(r =>
      r.studentId === studentId ? { ...r, status: newStatus } : r
    ));
    setHasChanges(true);
  };

  const markAllAs = (status) => {
    if (isViewOnly) return;
    setRecords(prev => prev.map(r => ({ ...r, status })));
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    if (isViewOnly) return;
    setIsSaving(true);
    try {
      await api.markAttendance({
        class: selectedClass,
        section: selectedSection,
        date: selectedDate,
        records: records.map(r => ({ studentId: r.studentId, status: r.status, remarks: null })),
      });
      toast.success(`Attendance saved for Class ${selectedClass}-${selectedSection}`);
      setHasChanges(false);
      fetchAttendance();
    } catch (error) {
      toast.error(error.message || 'Failed to save attendance');
    } finally {
      setIsSaving(false);
    }
  };

  const allStats = {
    total: records.length,
    present: records.filter(r => r.status === 'present').length,
    absent: records.filter(r => r.status === 'absent').length,
    late: records.filter(r => r.status === 'late').length,
    notMarked: records.filter(r => r.status === 'not_marked').length,
  };

  const filteredRecords = statusFilter === 'all'
    ? records
    : records.filter(r => r.status === statusFilter);

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-emerald-500 text-white';
      case 'absent': return 'bg-red-500 text-white';
      case 'late': return 'bg-amber-500 text-white';
      default: return 'bg-slate-200 text-slate-700';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present': return <Badge className="bg-emerald-500/10 text-emerald-600">Present</Badge>;
      case 'absent': return <Badge className="bg-red-500/10 text-red-600">Absent</Badge>;
      case 'late': return <Badge className="bg-amber-500/10 text-amber-600">Late</Badge>;
      case 'not_marked': return <Badge variant="outline">Not Marked</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div data-testid="attendance-page" className="space-y-6 animate-fade-in">
      <PageHeader
        title="Attendance"
        description={isViewOnly ? "View daily student attendance records" : "Mark and manage daily student attendance"}
      />

      {/* Attendance Percentage Banner */}
      <DataCard delay={0}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-500/10">
              <TrendingUp className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Attendance Percentage</p>
              <p className="text-3xl font-bold">{attPercentage?.percentage ?? 0}%</p>
              <p className="text-xs text-muted-foreground">
                {attPercentage?.present || 0} present out of {attPercentage?.total || 0} records
              </p>
            </div>
          </div>
          <Select value={attPeriod} onValueChange={setAttPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="att-page-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarterly">Quarter</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </DataCard>

      {/* Selection Controls */}
      <DataCard>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Class</label>
            <Select value={selectedClass} onValueChange={(v) => { setSelectedClass(v); setSelectedSection(''); }}>
              <SelectTrigger data-testid="attendance-class-select">
                <SelectValue placeholder="Select Class" />
              </SelectTrigger>
              <SelectContent>
                {availableClasses.map(c => (
                  <SelectItem key={c} value={c}>Class {c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Section</label>
            <Select value={selectedSection} onValueChange={setSelectedSection} disabled={!selectedClass}>
              <SelectTrigger data-testid="attendance-section-select">
                <SelectValue placeholder="Select Section" />
              </SelectTrigger>
              <SelectContent>
                {availableSections.map(s => (
                  <SelectItem key={s} value={s}>Section {s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Date</label>
            <Input type="date" value={selectedDate} onChange={handleDateChange} min={getMinDate()} max={getToday()} data-testid="attendance-date-input" />
            {dateError && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1" data-testid="date-error-message">
                <AlertTriangle className="w-3 h-3" /> {dateError}
              </p>
            )}
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Filter Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="attendance-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="present">Present</SelectItem>
                <SelectItem value="absent">Absent</SelectItem>
                <SelectItem value="late">Late</SelectItem>
                <SelectItem value="not_marked">Not Marked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </DataCard>

      {/* Stats */}
      {records.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatsCard title="Total Students" value={allStats.total} icon={Users} delay={0} />
          <StatsCard title="Present" value={allStats.present} icon={CheckCircle2} delay={0.05} />
          <StatsCard title="Absent" value={allStats.absent} icon={XCircle} delay={0.1} />
          <StatsCard title="Late" value={allStats.late} icon={Clock} delay={0.15} />
        </div>
      )}

      {/* Attendance Records */}
      {isLoading ? (
        <DataCard>
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
          </div>
        </DataCard>
      ) : filteredRecords.length > 0 ? (
        <DataCard>
          {/* Quick Actions - ONLY for teacher (not admin) */}
          {canEdit && (
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Quick Actions:</span>
                <Button variant="outline" size="sm" onClick={() => markAllAs('present')} data-testid="mark-all-present">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-emerald-500" /> All Present
                </Button>
                <Button variant="outline" size="sm" onClick={() => markAllAs('absent')} data-testid="mark-all-absent">
                  <XCircle className="w-3.5 h-3.5 mr-1.5 text-red-500" /> All Absent
                </Button>
              </div>
              {hasChanges && (
                <Badge variant="outline" className="border-amber-400 text-amber-600">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Unsaved changes
                </Badge>
              )}
            </div>
          )}

          {isViewOnly && (
            <div className="mb-4 pb-3 border-b border-slate-200 dark:border-slate-800">
              <Badge variant="outline" className="text-muted-foreground">
                <Filter className="w-3 h-3 mr-1" /> View Only Mode
              </Badge>
            </div>
          )}

          {/* Student List */}
          <div className="space-y-2">
            {filteredRecords.map((record) => (
              <div
                key={record.studentId}
                data-testid={`attendance-row-${record.studentId}`}
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-brand-light-blue/10 text-brand-deep-blue text-xs font-medium">
                      {getInitials(record.fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-foreground text-sm">{record.fullName}</p>
                    <p className="text-xs text-muted-foreground">Roll #{record.rollNumber} · {record.attendancePercentage}% overall</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {isViewOnly ? (
                    // Admin: Read-only status badge
                    getStatusBadge(record.status)
                  ) : (
                    // Teacher: Editable status toggles
                    ['present', 'late', 'absent'].map((status) => (
                      <button
                        key={status}
                        data-testid={`mark-${status}-${record.studentId}`}
                        onClick={() => updateStatus(record.studentId, status)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-all ${
                          record.status === status
                            ? getStatusColor(status)
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                        }`}
                      >
                        {status}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button - ONLY for teacher */}
          {canEdit && hasChanges && (
            <div className="flex justify-end mt-6 pt-4 border-t border-slate-200 dark:border-slate-800">
              <Button
                onClick={handleSubmit}
                disabled={isSaving}
                className="bg-brand-deep-blue hover:bg-brand-deep-blue/90 min-w-[160px]"
                data-testid="submit-attendance-btn"
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isSaving ? 'Saving...' : 'Save Attendance'}
              </Button>
            </div>
          )}
        </DataCard>
      ) : selectedClass && selectedSection ? (
        <DataCard>
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-heading font-semibold mb-2">No Students Found</h3>
            <p className="text-muted-foreground">No active students in Class {selectedClass}-{selectedSection}</p>
          </div>
        </DataCard>
      ) : (
        <DataCard>
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-heading font-semibold mb-2">Select Class & Section</h3>
            <p className="text-muted-foreground">Choose a class, section, and date to {isViewOnly ? 'view' : 'begin marking'} attendance</p>
          </div>
        </DataCard>
      )}
    </div>
  );
}
